/*
 * VPDB - Virtual Pinball Database
 * Copyright (C) 2019 freezy <freezy@vpdb.io>
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; either version 2
 * of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */

import type { DragPointSpec, Vec2 } from "../../shared/contracts";
import { DragPoint } from "../math/dragpoint";
import { Vertex2D } from "../math/vertex2d";
import { Vertex3D } from "../math/vertex3d";

/**
 * Parent class of the data of every table item.
 *
 * Upstream it reads a binary file. Here each subclass builds itself from a
 * spec with a `static fromSpec` function.
 */
export abstract class ItemData {
  public name: string;

  protected constructor(itemName: string) {
    this.name = itemName;
  }

  public getName(): string {
    return this.name;
  }
}

/** Physics values that a wall, rubber, ramp or target carries. */
export interface IPhysicalData {
  elasticity: number;
  elasticityFalloff?: number;
  friction: number;
  scatter: number;
  overwritePhysics: boolean;
  isCollidable: boolean;
  szPhysicsMaterial?: string;
}

/** Makes an engine point from a spec point. */
export function toVertex2D(p: Vec2): Vertex2D {
  return new Vertex2D(p.x, p.y);
}

/** Makes engine drag points from spec drag points. */
export function toDragPoints(points: readonly DragPointSpec[] | undefined): DragPoint[] {
  return (points ?? []).map(
    (p) =>
      new DragPoint({
        vertex: new Vertex3D(p.vertex.x, p.vertex.y, p.vertex.z),
        fSmooth: p.fSmooth ?? false,
        fSlingshot: p.fSlingshot ?? false,
        fAutoTexture: p.fAutoTexture ?? true,
        texturecoord: p.texturecoord ?? 0,
      }),
  );
}

/**
 * The signed area of a closed outline of drag points, times 2.
 * The y axis grows down the table. A positive value means clockwise on the screen.
 */
export function outlineArea(points: readonly DragPoint[]): number {
  let sum = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i]?.vertex;
    const b = points[(i + 1) % points.length]?.vertex;
    if (a && b) {
      sum += a.x * b.y - b.x * a.y;
    }
  }
  return sum;
}

/**
 * Returns the outline of a solid wall in the order that the collision code needs.
 *
 * The collision code lets a ball hit a wall edge only from the side that the edge normal
 * points to. The normal must point away from the wall. This is the case for an outline that
 * runs counter-clockwise on the screen. The table data runs clockwise (see `WallSpec`). The
 * engine reverses a clockwise outline, so both orders work.
 *
 * A slingshot flag belongs to the edge that starts at its point. The flags move with the edges.
 */
export function solidOutline(points: DragPoint[]): DragPoint[] {
  if (points.length < 3 || outlineArea(points) <= 0) {
    return points;
  }
  const count = points.length;
  return points.map((p, i) => {
    const source = points[count - 1 - i] as DragPoint;
    const edge = points[(count - 2 - i + count) % count] as DragPoint;
    return new DragPoint({
      vertex: source.vertex,
      fSmooth: source.fSmooth,
      fSlingshot: edge.fSlingshot,
      fAutoTexture: source.fAutoTexture,
      texturecoord: source.texturecoord,
    });
  });
}
